import React from 'react';
// Added 'Navigate' to imports for redirection
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { NotificationProvider } from './context/NotificationContext';

import Navbar from './components/Navbar';
import Home from './pages/Home';
import Marketplace from './pages/Marketplace';
import Login from './pages/Login';
import Register from './pages/Register';
import CompanyDashboard from './pages/CompanyDashboard';
import SetterDashboard from './pages/SetterDashboard';
import AdminDashboard from './pages/AdminDashboard';
import HowItWorks from './pages/HowItWorks';

const ScrollToTop = () => {
  const { pathname } = useLocation();
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

// --- NEW COMPONENT: PROTECTED ROUTE ---
// This acts as a security guard for your routes
const ProtectedRoute = ({ children, allowedRoles }) => {
  const token = localStorage.getItem('token');
  const userRole = Number(localStorage.getItem('role')); // Ensure it's a number

  // 1. Check if User is Logged In
  if (!token) {
    return <Navigate to="/login" replace />;
  }

  // 2. Check if User has the Correct Role
  // allowedRoles is an array, e.g., [2] for Company
  if (allowedRoles && !allowedRoles.includes(userRole)) {
    // If they are in the wrong place, redirect them to THEIR correct dashboard
    if (userRole === 1) return <Navigate to="/admin/dashboard" replace />;
    if (userRole === 2) return <Navigate to="/company/dashboard" replace />;
    if (userRole === 3) return <Navigate to="/setter/dashboard" replace />;
    return <Navigate to="/" replace />;
  }

  // 3. If passed checks, render the dashboard
  return children;
};

// --- NEW COMPONENT: SMART DASHBOARD REDIRECT ---
// Catches anyone going to just "/dashboard" and routes them correctly
const DashboardRedirect = () => {
  const role = Number(localStorage.getItem('role'));
  if (role === 1) return <Navigate to="/admin/dashboard" replace />;
  if (role === 2) return <Navigate to="/company/dashboard" replace />;
  if (role === 3) return <Navigate to="/setter/dashboard" replace />;
  return <Navigate to="/login" replace />;
};

function App() {
  return (
    <NotificationProvider>
      <Router>
        <ScrollToTop />
        
        <div className="min-h-screen bg-gray-50 flex flex-col">
          <Navbar />
          
          <main className="flex-grow">
            <Routes>
              {/* Public Routes */}
              <Route path="/" element={<Home />} />
              <Route path="/marketplace" element={<Marketplace />} />
              <Route path="/how-it-works" element={<HowItWorks />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              
              {/* --- PROTECTED ROUTES --- */}
              
              {/* Only Role 2 (Company) can access this */}
              <Route 
                path="/company/dashboard" 
                element={
                  <ProtectedRoute allowedRoles={[2]}>
                    <CompanyDashboard />
                  </ProtectedRoute>
                } 
              />

              {/* Only Role 3 (Setter) can access this */}
              <Route 
                path="/setter/dashboard" 
                element={
                  <ProtectedRoute allowedRoles={[3]}>
                    <SetterDashboard />
                  </ProtectedRoute>
                } 
              />

              {/* Only Role 1 (Admin) can access this */}
              <Route 
                path="/admin/dashboard" 
                element={
                  <ProtectedRoute allowedRoles={[1]}>
                    <AdminDashboard />
                  </ProtectedRoute>
                } 
              />

              {/* Catch-all for generic "/dashboard" link */}
              <Route path="/dashboard" element={<DashboardRedirect />} />

              {/* 404 Fallback */}
              <Route path="*" element={<Navigate to="/" replace />} />
              
            </Routes>
          </main>
        </div>
      </Router>
    </NotificationProvider>
  );
}

export default App;